import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FileCode2, PlayCircle, CheckCircle2, AlertTriangle, ShieldCheck, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { http } from "../../lib/api";
import { DEFAULT_REGO } from "../../lib/constants";
import { Panel, Btn, Overline } from "../ui";
import { useI18n, pickLang } from "../../lib/i18n";

const RISE_FROM = { opacity: 0, y: 14 };
const RISE_TO = { opacity: 1, y: 0 };

export default function PolicyStudio({ incoming, onConsumed }) {
  const { t, lang } = useI18n();
  const [rego, setRego] = useState(DEFAULT_REGO);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [source, setSource] = useState(null);

  useEffect(() => {
    if (!incoming?.rego) return;
    setRego(incoming.rego);
    setSource(incoming);
    setResult(null);
    onConsumed?.();
  }, [incoming, onConsumed]);

  const validate = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await http.post("/policy/validate", { rego });
      setResult(data);
      if (data.compiled) toast.success("策略语法校验通过 · Rules compiled");
      else toast.error("Policy failed compilation");
    } catch {
      toast.error("Validation error");
    } finally {
      setBusy(false);
    }
  };

  const enforcing = result?.status === "ENFORCING";

  return (
    <div className="space-y-6" data-testid="tab-policy">
      <div>
        <Overline>VCO Policy Studio</Overline>
        <h1 className="font-heading text-2xl font-light tracking-tight text-slate-100 mt-1">动态策略与规约 (Rego / OPA)</h1>
        <p className="text-sm text-slate-400 mt-1">编译通过的规则将自动同步给 Gateway 零信任校验引擎。</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-12">
        <div className="lg:col-span-7">
          {source && (
            <div
              className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-aegis-purple/35 bg-aegis-purple/[0.06] px-4 py-2.5"
              data-testid="policy-incoming-banner"
            >
              <Wand2 size={14} className="text-aegis-purple" />
              <span className="font-mono text-[11px] text-aegis-purple">{source.draft_id}</span>
              <span className="text-[12px] text-slate-300">{pickLang(source, "title", lang)}</span>
              <span className="ml-auto font-mono text-[10px] text-slate-400">{t("act.incoming")}</span>
            </div>
          )}
          <Panel icon={FileCode2} title="Rego 策略编辑器" subtitle="OPA standard" accent="cyan"
            actions={<Btn variant="cyan" onClick={validate} disabled={busy} testid="policy-validate-btn"><PlayCircle size={15} /> {busy ? "编译中…" : "测试编译 Rego"}</Btn>}
          >
            <textarea
              value={rego}
              onChange={(e) => setRego(e.target.value)}
              spellCheck={false}
              data-testid="policy-editor"
              className="h-[420px] w-full resize-none rounded-lg border border-edge well p-4 font-mono text-[12.5px] leading-relaxed text-aegis-emerald/90 focus:outline-none focus:ring-1 focus:ring-slate-500"
            />
          </Panel>
        </div>

        <div className="lg:col-span-5">
          <Panel icon={ShieldCheck} title="AI 策略规约校验器" subtitle="Gemini 3 Pro static review" accent="purple" testid="policy-result-panel">
            {!result && (
              <div className="rounded-lg border border-dashed border-edge py-16 text-center text-sm text-slate-400">
                运行“测试编译 Rego”以获得静态研判结果。
              </div>
            )}
            {result && (
              <motion.div initial={RISE_FROM} animate={RISE_TO} className="space-y-4">
                <div
                  className={`flex items-center gap-3 rounded-lg border p-4 ${
                    enforcing ? "border-aegis-emerald/40 bg-aegis-emerald/10" : "border-aegis-red/40 bg-aegis-red/10"
                  }`}
                >
                  {enforcing ? <CheckCircle2 className="text-aegis-emerald" size={22} /> : <AlertTriangle className="text-aegis-red" size={22} />}
                  <div>
                    <p className={`font-heading text-sm ${enforcing ? "text-aegis-emerald" : "text-aegis-red"}`}>
                      零信任强制规约状态：{result.status}
                    </p>
                    <p className="text-xs text-slate-400">{result.message}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg border border-edge well px-3 py-2">
                    <Overline>Rules Detected</Overline>
                    <p className="font-mono text-lg tabular text-slate-100">{result.rule_count}</p>
                  </div>
                  <div className="rounded-lg border border-edge well px-3 py-2">
                    <Overline>Compiled</Overline>
                    <p className={`font-mono text-lg ${result.compiled ? "text-aegis-emerald" : "text-aegis-red"}`}>
                      {String(result.compiled)}
                    </p>
                  </div>
                </div>

                {result.findings?.length > 0 && (
                  <div>
                    <Overline>Findings</Overline>
                    <ul className="mt-2 space-y-1.5">
                      {result.findings.map((f) => (
                        <li key={f} className="flex gap-2 text-[13px] text-slate-300">
                          <span className="text-aegis-amber">›</span>
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {result.hardening_suggestion && (
                  <div className="rounded-lg border border-aegis-purple/25 bg-aegis-purple/5 p-3 text-[13px] text-slate-200">
                    <span className="text-aegis-purple font-mono text-[11px] uppercase mr-2">Harden</span>
                    {result.hardening_suggestion}
                  </div>
                )}
              </motion.div>
            )}
          </Panel>
        </div>
      </div>
    </div>
  );
}
